import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Mail } from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { Avatar } from "@/components/Layout";

const fmtDate = (iso) => new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });

function SubRow({ sub, onChange }) {
  const [busy, setBusy] = useState(false);
  const c = sub.creator;
  const canceling = sub.status === "canceling";
  const act = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/subscriptions/${c.id}/${canceling ? "resume" : "cancel"}`);
      onChange({ ...sub, ...data });
      toast.success(canceling ? `Membership to @${c.username} resumed` : `Canceled. You keep access until ${fmtDate(data.access_until)}`);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-center gap-4 px-5 py-4" data-testid={`subscription-row-${c.username}`}>
      <Link to={`/u/${c.username}`} className="flex min-w-0 basis-full items-center gap-3 sm:flex-1 sm:basis-auto">
        <Avatar user={c} size="h-10 w-10" />
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold">{c.name}</p>
          <p className="text-xs text-gray-500">@{c.username} · since {fmtDate(sub.since)}</p>
        </div>
      </Link>
      <div className="text-right max-sm:mr-auto max-sm:text-left">
        <p className="text-sm tabular-nums">${sub.amount}<span className="text-gray-500"> / mo</span></p>
        <p className={`text-[11px] ${canceling ? "text-[#FF2E4D]" : "text-gray-500"}`} data-testid="subscription-status">
          {canceling ? `Ends ${fmtDate(sub.access_until)}` : "Renews monthly"}
        </p>
      </div>
      <div className="flex gap-2">
        <Link to={`/messages/${c.username}`} aria-label="Message" data-testid="subscription-message-btn"
          className="rounded-sm border border-[#2A2E37] bg-white/5 p-2 transition-colors hover:bg-white/10"><Mail className="h-4 w-4" strokeWidth={1.75} /></Link>
        <button onClick={act} disabled={busy} data-testid={canceling ? "subscription-resume-btn" : "subscription-cancel-btn"}
          className={`flex min-w-[84px] items-center justify-center gap-1.5 rounded-sm border px-3 py-2 text-xs font-medium transition-colors disabled:opacity-60 ${
            canceling ? "border-white bg-white text-[#090A0C] hover:bg-gray-200" : "border-[#2A2E37] bg-white/5 text-gray-200 hover:border-[#FF2E4D]/60 hover:text-[#FF2E4D]"}`}>
          {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}{canceling ? "Resume" : "Cancel"}
        </button>
      </div>
    </div>
  );
}

export default function Subscriptions() {
  const [subs, setSubs] = useState(null);
  useEffect(() => { api.get("/me/subscriptions").then((r) => setSubs(r.data)); }, []);
  const total = (subs || []).filter((s) => s.status === "active").reduce((a, s) => a + s.amount, 0);

  return (
    <div className="mx-auto max-w-3xl px-4 py-10 md:px-8 md:py-14" data-testid="subscriptions-page">
      <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#FF2E4D]">Memberships</p>
      <h1 className="mt-2 text-2xl font-semibold tracking-tight sm:text-3xl">My subscriptions</h1>
      {subs && subs.length > 0 && <p className="mt-1 text-sm text-gray-400" data-testid="subscriptions-total">${total.toFixed(2)} / month across {subs.length} creator{subs.length === 1 ? "" : "s"}</p>}
      <div className="mt-8 divide-y divide-[#2A2E37] rounded-md border border-[#2A2E37] bg-[#121418]">
        {subs === null && <p className="px-5 py-8 text-center text-sm text-gray-500">Loading...</p>}
        {subs?.length === 0 && (
          <p className="px-5 py-10 text-center text-sm text-gray-500" data-testid="subscriptions-empty">
            You're not subscribed to anyone yet. <Link to="/explore" className="text-[#FF2E4D] hover:underline">Explore creators</Link>
          </p>
        )}
        {subs?.map((s) => <SubRow key={s.creator.id} sub={s} onChange={(n) => setSubs((x) => x.map((y) => (y.creator.id === n.creator.id ? n : y)))} />)}
      </div>
      <p className="mt-4 text-xs text-gray-500">Canceling stops renewal. You keep access until the end of the period you paid for.</p>
    </div>
  );
}
